import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronRight, Landmark } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  type AccountRef,
  type Balance,
  Unauthorized,
  listAccounts,
  listBalances,
  listTransactions,
} from '@/lib/api';
import { formatAmount } from '@/lib/format';

const REVIEW_PAGE = 200;

/** Landing view: balances per currency, the review queue size, and each connected
 * bank's accounts with their closing and intraday booked balances. Selecting a bank
 * or an account opens its transactions. */
export function Banks({
  onOpen,
  onAuthError,
}: {
  onOpen: (source: string | null, account?: number) => void;
  onAuthError: () => void;
}) {
  const [accounts, setAccounts] = useState<AccountRef[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [toReview, setToReview] = useState<Map<string, { count: number; more: boolean }> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listAccounts(), listBalances()])
      .then(([a, b]) => {
        setAccounts(a);
        setBalances(b);
        const sources = [...new Set(a.map((x) => x.source))];
        return Promise.all(
          sources.map((source) =>
            listTransactions({ exposure: 'undecided', source, limit: REVIEW_PAGE }).then(
              (t) => [source, { count: t.items.length, more: t.nextCursor !== null }] as const,
            ),
          ),
        )
          .then((counts) => setToReview(new Map(counts)))
          .catch((e) => {
            if (e instanceof Unauthorized) onAuthError();
          });
      })
      .catch((e) => {
        if (e instanceof Unauthorized) onAuthError();
        else setError('Could not load banks.');
      })
      .finally(() => setLoading(false));
  }, [onAuthError]);

  const banks = useMemo(() => {
    const m = new Map<string, AccountRef[]>();
    for (const a of accounts) {
      const list = m.get(a.source) ?? [];
      list.push(a);
      m.set(a.source, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.provider_id.localeCompare(b.provider_id));
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [accounts]);

  const balance = useMemo(() => {
    const m = new Map<string, Balance>();
    for (const b of balances) m.set(`${b.account_id}:${b.balance_type}`, b);
    return (accountId: number, type: string) => m.get(`${accountId}:${type}`);
  }, [balances]);

  const totals = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of balances) {
      if (b.balance_type !== 'CLBD') continue;
      m.set(b.currency, (m.get(b.currency) ?? 0) + Number(b.signed_amount));
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [balances]);

  const closingDates = useMemo(
    () =>
      [...new Set(balances.filter((b) => b.balance_type === 'CLBD' && b.balance_date).map((b) => b.balance_date as string))].sort(),
    [balances],
  );
  const closingLabel =
    closingDates.length === 0
      ? 'Connected banks and balances'
      : closingDates.length === 1
        ? `Closing balances as of ${closingDates[0]}`
        : `Closing balances from ${closingDates[0]} to ${closingDates.at(-1)} (dates differ by account)`;

  const reviewTotal = useMemo(() => {
    if (!toReview) return null;
    let count = 0;
    let more = false;
    for (const v of toReview.values()) {
      count += v.count;
      more ||= v.more;
    }
    return { count, more };
  }, [toReview]);

  const reviewLabel = (v: { count: number; more: boolean }) => `${v.count}${v.more ? '+' : ''}`;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Overview</h1>
          <p className="text-sm text-muted-foreground">
            {closingLabel}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => onOpen(null)} className="gap-1">
          All transactions <ArrowRight className="size-4" aria-hidden />
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && !error && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {totals.map(([currency, total]) => (
            <Stat key={currency} label={`${currency} closing balance`}>
              {formatAmount(String(total), currency)}
            </Stat>
          ))}
          <Stat label="Accounts">
            {accounts.length}
            <span className="ml-1.5 text-sm font-normal text-muted-foreground">
              at {banks.length} bank{banks.length === 1 ? '' : 's'}
            </span>
          </Stat>
          <Stat label="To review">
            <button
              type="button"
              onClick={() => onOpen(null)}
              className="inline-flex cursor-pointer items-center gap-1 hover:underline"
            >
              {reviewTotal ? reviewLabel(reviewTotal) : '—'}
              <span className="text-sm font-normal text-muted-foreground">undecided</span>
            </button>
          </Stat>
        </div>
      )}

      {!loading && banks.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No banks connected yet.</p>
      )}

      {banks.map(([source, accts]) => (
        <div key={source} className="overflow-hidden rounded-xl border bg-card shadow-xs">
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
            <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Landmark className="size-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold leading-tight">{source.toUpperCase()}</h2>
              <p className="text-xs text-muted-foreground">
                {accts.length} account{accts.length === 1 ? '' : 's'}
              </p>
            </div>
            {(toReview?.get(source)?.count ?? 0) > 0 && (
              <Badge variant="secondary">{reviewLabel(toReview!.get(source)!)} to review</Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => onOpen(source)} className="ml-auto gap-1">
              Transactions <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="hidden px-4 py-2 font-medium sm:table-cell">Currency</th>
                <th className="px-4 py-2 text-right font-medium">Closing booked</th>
                <th className="hidden px-4 py-2 text-right font-medium md:table-cell">Intraday booked</th>
                <th className="w-10 px-2 py-2" aria-hidden />
              </tr>
            </thead>
            <tbody className="divide-y">
              {accts.map((a) => {
                const closing = balance(a.account_id, 'CLBD');
                const intraday = balance(a.account_id, 'ITBD');
                const currency = closing?.currency ?? intraday?.currency;
                return (
                  <tr
                    key={a.account_id}
                    onClick={() => onOpen(source, a.account_id)}
                    className="cursor-pointer hover:bg-muted/40"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs sm:text-sm">{a.provider_id}</div>
                      {a.bic && <div className="text-xs text-muted-foreground">{a.bic}</div>}
                    </td>
                    <td className="hidden px-4 py-2.5 text-muted-foreground sm:table-cell">{currency ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Amount b={closing} />
                    </td>
                    <td className="hidden px-4 py-2.5 text-right md:table-cell">
                      <Amount b={intraday} />
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">
                      <ChevronRight className="size-4" aria-hidden />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3 shadow-xs">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{children}</p>
    </div>
  );
}

function Amount({ b }: { b: Balance | undefined }) {
  if (!b) return <span className="text-muted-foreground">—</span>;
  const zero = Number(b.signed_amount) === 0;
  return (
    <div>
      <div className={`font-medium tabular-nums ${zero ? 'text-muted-foreground' : ''}`}>
        {formatAmount(b.signed_amount, b.currency)}
      </div>
      {b.balance_date && <div className="text-xs text-muted-foreground tabular-nums">{b.balance_date}</div>}
    </div>
  );
}
